import { canPerformCustomTask, canViewAnyTableOrRole } from './customTablesVisibility';
import { canViewAnyAdminOptions, canEditAdminArea } from './adminVisibility';
import {
  ADMIN_DASHBOARD,
  OVERALL_ROLE,
  OVERALL_TABLE,
  PLATFORM_CALENDAR,
  PLATFORM_ORG_CHART,
  PLATFORM_PEOPLE,
  PLATFORM_TEAM,
  RECORD_ADDITIONAL_INFO,
  NEGLECTED_STAGES,
} from './constants';

import { isAdmin, isAdminOrSuperAdmin, isManager, isSuperAdmin } from './userTypes';
import { canPerformPlatformTask, canViewPlatformArea } from './platformVisibility';
import { canViewTeam } from './teamVisibility';
import { canViewEmployeeInfo } from './employeeRecordVisibility';
import {
  canViewTimeOffPaywall,
  PAYWALL_ORG_CHART,
  PAYWALL_TIME_OFF,
  payWallChecker,
} from '@ApplicationWide/permissions/payWallPermissions';
import { isCompanySettingsEnabled } from '@ApplicationWide/permissions/companySettings';
import { UserStage } from '@Utils/enums/userStage';

const USER_ACTIVE_STATE = 'active';

export const canViewOverallPlatformArea = (
  area,
  loggedInUser,
  employee = loggedInUser,
  company = null,
  checkOwnPermissions = false,
  isMobile = false,
  skipCompanySettingsCheck = false,
  tabIdentifier = null,
) => {
  if (area === PAYWALL_TIME_OFF) {
    return canViewTimeOffPaywall(loggedInUser, company);
  }

  if (area === PLATFORM_CALENDAR && isMobile) {
    return false;
  }

  if (!skipCompanySettingsCheck && !isCompanySettingsEnabled(area, company)) {
    return false;
  }

  if (area === OVERALL_TABLE || area === OVERALL_ROLE) {
    return canViewAnyTableOrRole(loggedInUser, employee, company, checkOwnPermissions);
  }

  if (area === PLATFORM_TEAM) return canViewTeam(employee, company);

  return canViewPlatformArea(area, loggedInUser, employee, tabIdentifier);
};

export const canAccessFieldsForReport = (area, loggedInUser) => {
  if (isSuperAdmin(loggedInUser)) return true;
  let section = area;
  if (area === 'additionalFields') {
    section = RECORD_ADDITIONAL_INFO;
  }
  return canViewEmployeeInfo(section, loggedInUser, loggedInUser) && isAdmin(loggedInUser);
};

export const canViewAdminNavigation = (loggedInUser) =>
  isAdminOrSuperAdmin(loggedInUser) && canViewAnyAdminOptions(loggedInUser);

export const canManagerOffboard = (user, employee) => {
  return isManager(user) && canPerformCustomTask('canOffboardReports', user, employee);
};

export const canAdminOffboard = (user, employee) => {
  return isAdmin(user) && canPerformPlatformTask('canOffboardUsers', user, employee) && !isSuperAdmin(employee);
};

const isCurrentUserReadyToOffboard = (userBeingViewed) =>
  !userBeingViewed.terminationDate &&
  !userBeingViewed.lastDayWorked &&
  userBeingViewed.state === USER_ACTIVE_STATE &&
  !NEGLECTED_STAGES.includes(userBeingViewed.currentStage);

export const canOffboard = (user, employee) => {
  return (
    (canManagerOffboard(user, employee) || canAdminOffboard(user, employee) || isSuperAdmin(user)) &&
    isCurrentUserReadyToOffboard(employee)
  );
};

/**
 *  As described by Zain Bin Arshad:
 *    "following conditions should be fulfilled:
 *      Current stage of the respective user should be 'departed'
 *      Current user should have 'view_and_edit' permission for dashboard
 *      Current user should be an admin or super_admin#
 */
export const canRehire = (user, employee) =>
  employee.currentStage === UserStage.DEPARTED && canEditAdminArea(ADMIN_DASHBOARD, user);

export const canViewNonAdminRoute = (route, loggedInUser, company) => {
  if (route.area === PLATFORM_ORG_CHART) {
    return isCompanySettingsEnabled(route.area, company) && canViewPlatformArea(PLATFORM_PEOPLE, loggedInUser);
  }

  if (route.area === PAYWALL_ORG_CHART) {
    return !isCompanySettingsEnabled(PLATFORM_ORG_CHART, company) && payWallChecker(loggedInUser, company, route.area);
  }
  return canViewOverallPlatformArea(route.area, loggedInUser, loggedInUser, company);
};
